import { useState } from 'react';
import type { VendorWithRelations } from '@peles/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateVendor, useVendorCategories } from '../../hooks/useVendors';
import { addVendorCategory } from '../../api/vendors';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { extractApiErrorMessage } from '../../utils/apiError';

export interface QuickVendorFormProps {
  /** The category it starts in — the need's, when asked from one. */
  defaultCategoryId?: string | null;
  /** Called with the vendor once it is saved. */
  onCreated: (vendor: VendorWithRelations) => void;
  onCancel: () => void;
}

/**
 * Just enough of a vendor to ask them for a quote — name, category and the
 * address the letter goes to — without leaving for the directory. The rest
 * (contacts, tax id, notes) is on the vendor's own page.
 */
export function QuickVendorForm({ defaultCategoryId, onCreated, onCancel }: QuickVendorFormProps) {
  const createVendor = useCreateVendor();
  const queryClient = useQueryClient();
  const { data: categories = [] } = useVendorCategories();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(defaultCategoryId ?? null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setError('');
    setSaving(true);
    try {
      const vendor = await createVendor.mutateAsync({
        businessName: name.trim(),
        email: email.trim() || undefined,
      });
      let saved: VendorWithRelations = {
        ...vendor,
        categories: vendor.categories ?? [],
        contacts: vendor.contacts ?? [],
      };
      if (categoryId) {
        try {
          saved = await addVendorCategory(vendor.id, {
            vendorCategoryId: categoryId,
            isPrimary: true,
          });
        } catch {
          // The vendor exists; a missing category only narrows where it is offered.
        }
        void queryClient.invalidateQueries({ queryKey: ['vendors'] });
      }
      onCreated(saved);
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Could not save the vendor'));
    } finally {
      setSaving(false);
    }
  };

  const emailProblem = email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  return (
    <div className="rounded-xl border border-primary-200 bg-primary-50/40 p-4 space-y-3">
      <h3 className="text-sm font-semibold text-gray-900">New vendor</h3>
      {error && <div className="px-3 py-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label
            htmlFor="quick-vendor-name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Name
          </label>
          <input
            id="quick-vendor-name"
            type="text"
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Bella Florals"
            className="w-full h-11 sm:h-10 px-3 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label
            htmlFor="quick-vendor-email"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Email
          </label>
          <input
            id="quick-vendor-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Where the request goes"
            aria-invalid={emailProblem ? true : undefined}
            className="w-full h-11 sm:h-10 px-3 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>
      <div>
        <label
          htmlFor="quick-vendor-category"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Category
        </label>
        <SearchableListSelect
          inputId="quick-vendor-category"
          items={categories}
          value={categoryId}
          onChange={(v) => setCategoryId((v as string) || null)}
          getValue={(c) => c.id}
          getLabel={(c) => c.name}
          placeholder="Choose a category"
          ariaLabel="Vendor category"
        />
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void save()}
          disabled={!name.trim() || !!emailProblem || saving}
          className="min-h-11 sm:min-h-9 px-4 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save vendor'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="min-h-11 sm:min-h-9 px-4 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-white"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
